import { ArrowBackRounded, ChevronRightRounded } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Link,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Link as RouterLink,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { EmailSuggestion } from "components/UI/AcxEmailChipInput";
import { KboFlowDebugService } from "services/KboFlowDebugService";
import { UserService } from "services/UserService";
import { useStore } from "utils/useStore";
import {
    KboFlowRunDebug,
    KboFlowRunOutput,
} from "../KboFlowDebug/KboFlowRunDebug";
import { shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { RunNavigation } from "./KboFlowRunNavigation";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import {
    formatRunDate,
    getDigestQuestions,
    getQuestionReport,
} from "./KboFlowRunDigestModels";
import { QuestionView } from "./KboFlowRunQuestionView";
import { WholeRun } from "./KboFlowRunWholeRun";
import { FindingsView } from "./KboFlowRunFindingsView";
import type { FlowStepDebugLocationState } from "../KboFlowStepDebug/FlowStepDebug";

const QUESTION_PARAM = "question";

function isRunOutput(
    value: KboFlowRunDebug["runOutput"],
): value is KboFlowRunOutput {
    return !!value && typeof value === "object";
}

function DetailSkeleton() {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={150} />
            <Skeleton variant="rounded" height={260} />
            <Skeleton variant="rounded" height={220} />
        </Box>
    );
}

const KboFlowRunDetail: React.FC<{ orgId: string }> = ({ orgId }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { runId } = useParams<{ runId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const service = useMemo(() => new KboFlowDebugService(), []);
    const userService = useMemo(() => new UserService(), []);
    const messageStore = useStore(MessageStore);
    const [run, setRun] = useState<KboFlowRunDebug>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [emailSuggestions, setEmailSuggestions] = useState<EmailSuggestion[]>(
        [],
    );
    const [emailSuggestionsLoading, setEmailSuggestionsLoading] =
        useState(false);
    const [emailSubmitting, setEmailSubmitting] = useState(false);

    useEffect(() => {
        if (!runId) {
            setError("No run id was supplied.");
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        setLoading(true);
        setError(undefined);
        service
            .getRun(runId, controller.signal)
            .then(setRun)
            .catch((caught: unknown) => {
                if (!controller.signal.aborted) {
                    setError(
                        caught instanceof Error
                            ? caught.message
                            : "This KBo Flow run could not be loaded.",
                    );
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [runId, service]);

    // Recipient suggestions for the "send report" picker. Convenience only — typing
    // an address still works if this fails, so a load error just clears suggestions.
    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        setEmailSuggestionsLoading(true);
        userService
            .getUsersForSharing(orgId)
            .then((users) => {
                if (cancelled) return;
                setEmailSuggestions(
                    users
                        .filter(
                            (u) =>
                                u.email &&
                                u.email !== "acxmachine@authenticx.com",
                        )
                        .map((u) => ({
                            email: u.email,
                            name: `${u.firstName} ${u.lastName}`.trim(),
                        })),
                );
            })
            .catch(() => {
                if (!cancelled) setEmailSuggestions([]);
            })
            .finally(() => {
                if (!cancelled) setEmailSuggestionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, userService]);

    const sendReportEmail = useCallback(
        async (recipients: string[], reportIndex: number): Promise<boolean> => {
            if (!runId) return false;
            const reportId = isRunOutput(run?.runOutput)
                ? run.runOutput.reports?.[reportIndex]?.reportId
                : undefined;
            if (!reportId) {
                messageStore.logError(
                    "This legacy report has no persisted email report ID and cannot be sent.",
                );
                return false;
            }
            try {
                setEmailSubmitting(true);
                const { sentTo } = await service.sendReportEmail(
                    runId,
                    recipients,
                    reportId,
                );
                messageStore.logMessage(
                    `Report sent to ${sentTo.join(", ")}`,
                    "success",
                );
                return true;
            } catch (caught) {
                messageStore.logError(
                    caught instanceof Error
                        ? caught.message
                        : "Failed to send report email",
                );
                return false;
            } finally {
                setEmailSubmitting(false);
            }
        },
        [run, runId, service, messageStore],
    );

    const openStepDebug = useCallback(
        (
            target: Pick<
                FlowStepDebugLocationState,
                "stepId" | "nodeId"
            >,
        ) => {
            if (!run?.id) return;
            navigate(`/admin/organizations/${orgId}/flow-step-debug`, {
                state: {
                    sourceRunId: run.id,
                    ...target,
                } as FlowStepDebugLocationState,
            });
        },
        [navigate, orgId, run?.id],
    );

    const output = isRunOutput(run?.runOutput) ? run.runOutput : undefined;
    const questions = output ? getDigestQuestions(output) : [];
    const requestedQuestionId = searchParams.get(QUESTION_PARAM) ?? undefined;
    const selectedQuestion = questions.find(
        (question) => question.id === requestedQuestionId,
    );
    const selectedQuestionIndex = selectedQuestion
        ? questions.indexOf(selectedQuestion)
        : -1;
    const selectedQuestionReport =
        output && selectedQuestion
            ? getQuestionReport(output, selectedQuestion)
            : undefined;
    const selectedQuestionHasFindings = !!(
        output?.exploration?.nodes?.length &&
        selectedQuestionReport?.data?.findings?.length
    );

    const setQuestion = (questionId?: string) => {
        const next = new URLSearchParams(searchParams);
        if (questionId) next.set(QUESTION_PARAM, questionId);
        else next.delete(QUESTION_PARAM);
        setSearchParams(next, { replace: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Box
            sx={{
                bgcolor: theme.palette.gray[50],
                minHeight: "100%",
                p: { xs: 2, md: 4 },
                width: "100%",
            }}
        >
            <Box sx={{ maxWidth: 1500, mx: "auto" }}>
                <Breadcrumbs
                    separator={
                        <ChevronRightRounded
                            sx={{
                                color: theme.palette.gray[400],
                                fontSize: 18,
                            }}
                        />
                    }
                >
                    <Link
                        component={RouterLink}
                        to={`/admin/organizations/${orgId}`}
                        underline="none"
                        color="text.secondary"
                        sx={{ fontSize: 14, fontWeight: 700 }}
                    >
                        Admin
                    </Link>
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: 14, fontWeight: 700 }}
                    >
                        KBo
                    </Typography>
                    <Link
                        component={RouterLink}
                        to={`/admin/organizations/${orgId}/kbo-flow-runs`}
                        underline="none"
                        color="text.secondary"
                        sx={{ fontSize: 14, fontWeight: 700 }}
                    >
                        Flow runs
                    </Link>
                    <Typography
                        sx={{
                            color: pageColors.ink,
                            fontSize: 14,
                            fontWeight: 700,
                        }}
                    >
                        {output
                            ? formatRunDate(output.startedAtUtc)
                            : shortId(runId)}
                    </Typography>
                </Breadcrumbs>

                <Box sx={{ mt: 2.25, mb: 3 }}>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1,
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            aria-label="Back to KBo Flow runs"
                            onClick={() =>
                                navigate(
                                    `/admin/organizations/${orgId}/kbo-flow-runs`,
                                )
                            }
                            sx={{
                                alignItems: "center",
                                bgcolor: "transparent",
                                border: 0,
                                color: "text.secondary",
                                cursor: "pointer",
                                display: "flex",
                                p: 0,
                            }}
                        >
                            <ArrowBackRounded />
                        </Box>
                        <Typography
                            component="h1"
                            sx={{
                                color: pageColors.ink,
                                fontSize: { xs: 29, md: 38 },
                                fontWeight: 750,
                                letterSpacing: "-0.03em",
                            }}
                        >
                            How KBo reached this result
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            color: pageColors.muted,
                            fontSize: 16,
                            lineHeight: 1.55,
                            mt: 0.75,
                            maxWidth: 1100,
                        }}
                    >
                        Start with what KBo sent, then drill into the fan-out
                        path and model reasoning behind every result.
                    </Typography>
                </Box>

                {error ? (
                    <Alert severity="error">{error}</Alert>
                ) : loading ? (
                    <DetailSkeleton />
                ) : !output ? (
                    <Alert severity="warning">
                        This run output could not be parsed as a KBo Flow run.
                    </Alert>
                ) : (
                    <>
                        <RunNavigation
                            output={output}
                            questions={questions}
                            selectedQuestionId={selectedQuestion?.id}
                            onSelect={setQuestion}
                        />
                        <Box sx={{ mt: 2.5 }}>
                            {requestedQuestionId && !selectedQuestion ? (
                                <Alert severity="warning">
                                    That question is not part of this run.{" "}
                                    <Link
                                        component="button"
                                        onClick={() => setQuestion()}
                                    >
                                        Show the whole run
                                    </Link>
                                    .
                                </Alert>
                            ) : selectedQuestion ? (
                                selectedQuestionHasFindings &&
                                selectedQuestionReport ? (
                                    <FindingsView
                                        output={output}
                                        runId={run?.id}
                                        report={selectedQuestionReport}
                                        emailSuggestions={emailSuggestions}
                                        emailSuggestionsLoading={
                                            emailSuggestionsLoading
                                        }
                                        emailSubmitting={emailSubmitting}
                                        onSendReportEmail={sendReportEmail}
                                        onOpenStepDebug={openStepDebug}
                                    />
                                ) : (
                                    <QuestionView
                                        output={output}
                                        question={selectedQuestion}
                                        questionIndex={selectedQuestionIndex}
                                        emailSuggestions={emailSuggestions}
                                        emailSuggestionsLoading={
                                            emailSuggestionsLoading
                                        }
                                        emailSubmitting={emailSubmitting}
                                        onSendReportEmail={sendReportEmail}
                                    />
                                )
                            ) : (
                                <WholeRun
                                    output={output}
                                    questions={questions}
                                    onSelectQuestion={setQuestion}
                                    persistedStatus={run?.status}
                                />
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default KboFlowRunDetail;
